"use client";

import React from "react";
import { MapPin, Phone, Mail, Globe, ShieldCheck, Leaf, Truck, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurGlobalPresence() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-global-presence" className="dark bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/global_logistics_bg.png')] bg-cover bg-center bg-no-repeat opacity-10 pointer-events-none" aria-hidden="true" />
      
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/50 to-background/90 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-primary-foreground">
            Our Global Presence
          </h2>
          <p className="text-lg text-muted-foreground">
            Bridging the gap between the USA and India with a robust, transparent, and efficient supply chain network.
          </p>
        </div>

        {}
        <div ref={cardsRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 transition-all duration-1000 delay-200 ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          
          {}
          <Card className="bg-card/40 backdrop-blur-xl border-border/50 shadow-2xl overflow-hidden flex flex-col h-full">
            <div className="relative h-48 w-full overflow-hidden bg-muted/20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/apex_nc_headquarters_map.png" alt="Apex, NC Headquarters Map" className="w-full h-full object-cover opacity-80 mix-blend-luminosity hover:mix-blend-normal transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent" />
              <div className="absolute bottom-4 left-6 flex items-center gap-2">
                <div className="bg-primary/20 p-2 rounded-full backdrop-blur-sm border border-primary/30">
                  <MapPin className="size-5 text-primary-foreground" />
                </div>
                <span className="font-semibold text-primary-foreground tracking-wide">USA Operations</span>
              </div>
            </div>
            
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl text-primary-foreground">Apex, NC Headquarters</CardTitle>
            </CardHeader>
            
            <CardContent className="flex-grow flex flex-col gap-6">
              <p className="text-muted-foreground">
                Our central hub for North American distribution, client relations, and strategic planning. We ensure seamless import processes and dedicated customer support for our US partners.
              </p>
              
              <div className="space-y-4 mt-auto pt-6 border-t border-border/30">
                <div className="flex items-start gap-3">
                  <MapPin className="size-5 text-secondary mt-0.5 shrink-0" />
                  <span className="text-card-foreground">Apex, North Carolina, USA</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="size-5 text-secondary shrink-0" />
                  <span className="text-card-foreground">+1 555-123-4567</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="size-5 text-secondary shrink-0" />
                  <span className="text-card-foreground">info@cosmiexim.com</span>
                </div>
              </div>
              
              <Button variant="outline" className="w-full mt-4 border-border/50 hover:bg-primary/20 hover:text-primary-foreground transition-colors">
                Contact US Office
              </Button>
            </CardContent>
          </Card>

          {}
          <Card className="bg-card/40 backdrop-blur-xl border-border/50 shadow-2xl overflow-hidden flex flex-col h-full">
            <div className="relative h-48 w-full overflow-hidden bg-muted/20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/hyderabad_sourcing_hub_map.png" alt="Hyderabad Sourcing Hub Map" className="w-full h-full object-cover opacity-80 mix-blend-luminosity hover:mix-blend-normal transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent" />
              <div className="absolute bottom-4 left-6 flex items-center gap-2">
                <div className="bg-secondary/20 p-2 rounded-full backdrop-blur-sm border border-secondary/30">
                  <Globe className="size-5 text-secondary-foreground" />
                </div>
                <span className="font-semibold text-primary-foreground tracking-wide">India Operations</span>
              </div>
            </div>
            
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl text-primary-foreground">Hyderabad Sourcing Hub</CardTitle>
            </CardHeader>
            
            <CardContent className="flex-grow flex flex-col gap-6">
              <p className="text-muted-foreground">
                The heart of our procurement network. We maintain direct relationships with local farmers and manufacturers to guarantee authenticity, quality, and ethical sourcing.
              </p>
              
              <div className="space-y-4 mt-auto pt-6 border-t border-border/30">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="size-5 text-secondary mt-0.5 shrink-0" />
                  <span className="text-card-foreground">Rigorous quality control & authentic sourcing</span>
                </div>
                <div className="flex items-start gap-3">
                  <Leaf className="size-5 text-secondary mt-0.5 shrink-0" />
                  <span className="text-card-foreground">Focus on eco-friendly & sustainable practices</span>
                </div>
                <div className="flex items-start gap-3">
                  <Truck className="size-5 text-secondary mt-0.5 shrink-0" />
                  <span className="text-card-foreground">Transparent and reliable international logistics</span>
                </div>
              </div>
              
              <Button className="w-full mt-4 bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-colors group">
                Partner with India Hub
                <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

        </div>
      </div>
    </section>;
}